import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Detects whether the on-screen keyboard is open on mobile devices.
 *
 * On focusin: starts polling visualViewport.height every frame via rAF
 * so keyboardHeight updates continuously during the keyboard animation.
 * On focusout: stops polling and resets.
 *
 * @returns {{ isKeyboardOpen: boolean, keyboardHeight: number }}
 */
export function useKeyboardOpen() {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const blurTimerRef = useRef(null);
  const rafRef = useRef(null);
  const pollingRef = useRef(false);
  // Store full viewport height when keyboard is closed
  const fullHeightRef = useRef(
    typeof window !== 'undefined'
      ? (window.visualViewport?.height || window.innerHeight)
      : 0
  );

  // Poll visualViewport every frame to get real-time keyboard height
  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;

    const poll = () => {
      if (!pollingRef.current) return;
      const vv = window.visualViewport;
      const currentHeight = vv ? vv.height : window.innerHeight;
      const kbH = Math.max(0, fullHeightRef.current - currentHeight);
      setKeyboardHeight(kbH);
      rafRef.current = requestAnimationFrame(poll);
    };
    rafRef.current = requestAnimationFrame(poll);
  }, []);

  const stopPolling = useCallback(() => {
    pollingRef.current = false;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onFocusIn = (e) => {
      const tag = e.target?.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') return;

      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }

      setIsKeyboardOpen(true);
      startPolling();
    };

    const onFocusOut = (e) => {
      const tag = e.target?.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') return;

      blurTimerRef.current = setTimeout(() => {
        blurTimerRef.current = null;
        const active = document.activeElement?.tagName;
        if (active === 'INPUT' || active === 'TEXTAREA') return;

        stopPolling();
        setIsKeyboardOpen(false);
        setKeyboardHeight(0);

        // Update full height reference now that keyboard is closed
        const vv = window.visualViewport;
        fullHeightRef.current = vv ? vv.height : window.innerHeight;
      }, 150);
    };

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);

    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      stopPolling();
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, [startPolling, stopPolling]);

  return { isKeyboardOpen, keyboardHeight };
}
